import React from 'react'
import moment, { Moment } from 'moment'
import numeral from 'numeral'
import { get } from 'lodash'
import Toolbar from './Toolbar'
import { SERVER_DATETIME_FMT } from '@constants'
import track from '@track'
import { Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  Card,
  Icon,
  PrettyDate,
  NewGridTable,
  GridTableStatusEnum,
  IGridTableColumn,
  IGridTableCell,
} from '@ui'
import { ILease, LeaseDateStates } from '@api'
import { ILeases } from './hooks/useLeases'
import { compact } from 'lodash'
import { NavigateFn } from '@reach/router'
import { useUser } from '@hooks'
import { sqFtToSqMeters } from '@utils'

interface Props {
  leases: ILeases
  navigate?: NavigateFn
}
interface LeaseProps {
  lease: ILease
}

const monthsUntil = (date: Moment) => {
  return date.diff(moment(), 'months', true)
}
const getColor = (months: number) => {
  if (months < 0) return LeaseDateStates.EXPIRED
  if (months <= 3) return LeaseDateStates.DANGER
  if (months <= 12) return LeaseDateStates.WARNING
}

const ExclamationIcon = ({ months }: { months: number }) => {
  if (months > 3) return null
  let content = 'Expiring soon'
  if (months < 0) content = 'Expired'
  return (
    <Tooltip2 content={content}>
      <Icon name="exclamation-triangle" className="mr1" />
    </Tooltip2>
  )
}

export default function TableView({ leases, navigate }: Props) {
  const { user } = useUser()

  const isSqFt = user?.context.units === 'sqft'

  const columns: IGridTableColumn<ILease>[] = [
    {
      label: 'Lease',
      sort: 'location',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>location of lease</span>

        const lease = (cell as IGridTableCell<ILease>).data

        return (
          <div
            onClick={() => {
              track.lease({ eventName: track.Event.ROW_CLICK })
              navigate && navigate(`${window.location.pathname}?leaseId=${lease.id}`)
            }}
          >
            <Lease lease={lease} />
          </div>
        )
      },
    },
    {
      label: 'Notice Date',
      sort: 'noticeDate',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>notice date</span>

        const lease = (cell as IGridTableCell<ILease>).data

        if (!lease.noticeDate) return null
        return <PrettyDate colorize value={lease.noticeDate} momentFormat="MMM DD, YYYY" />
      },
    },
    {
      label: 'Expiration Date',
      sort: 'endDate',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>expiration date</span>

        const lease = (cell as IGridTableCell<ILease>).data

        if (!lease.endDate) return null
        const endDate = moment(lease.endDate, SERVER_DATETIME_FMT)
        const months = monthsUntil(endDate)
        const color = getColor(months)

        return (
          <span style={{ color }}>
            <ExclamationIcon months={months} />
            <PrettyDate value={lease.endDate} momentFormat="MMM DD, YYYY" />
          </span>
        )
      },
    },
    {
      label: 'Monthly Cost',
      sort: 'cost',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>Area</span>

        const lease = (cell as IGridTableCell<ILease>).data

        return <>{numeral(lease.monthlyCost).format('$0,0.00')}</>
      },
    },
    {
      label: 'Annual Cost',
      sort: 'cost~',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>Area</span>

        const lease = (cell as IGridTableCell<ILease>).data

        return <>{numeral(lease.cost).format('$0,0.00')}</>
      },
    },
    {
      label: isSqFt ? (
        "Area ('SqFt')"
      ) : (
        <span>
          Area M<sup>2</sup>
        </span>
      ),
      sort: 'leaseArea',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>Area</span>

        const lease = (cell as IGridTableCell<ILease>).data
        const value = isSqFt
          ? lease.metadata.leaseArea
          : sqFtToSqMeters(Number(lease.metadata.leaseArea || 0))

        return <>{numeral(value).format('0,0')}</>
      },
    },
  ]

  const format = (items: ILease[]) =>
    items.map((i) => ({
      data: i,
    }))

  const formated = format(leases.items)

  return (
    <Card>
      <Toolbar {...{ leases, navigate }} />
      <NewGridTable<ILease>
        status={leases.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        minCellWidth="auto"
        columns={columns}
        data={formated}
        sort={{
          ...leases.sort,
          onSort: (column) => {
            leases.sort.onChange(column)
            track.lease({
              eventName: track.Event.SORT,
              props: {
                columnName: column,
              },
            })
          },
        }}
        page={{
          ...leases.page,
          onNextPage: () => {
            leases.nextPage()
            track.lease({ eventName: track.Event.PAGE })
          },
        }}
        headerTop={52}
      />
    </Card>
  )
}

export const Lease = ({ lease }: LeaseProps) => {
  let title = lease.title || ''
  const additional =
    compact([
      get(lease, 'floor.building.name', get(lease, 'building.name')),
      get(lease, 'floor.name'),
      get(lease, 'metadata.suite'),
    ]).join(' - ') || ' No Location'

  title = `${title}, ${additional}`

  return (
    <div>
      <div className="table__link">{title}</div>
      <div style={{ color: 'var(--grey)' }}>
        {get(lease, 'floor.building.address', get(lease, 'building.address', 'No Address'))}
      </div>
    </div>
  )
}
